import type {ReactNode, RefObject, SyntheticEvent} from "react";
import {createContext, Suspense, useContext, useEffect, useRef} from "react";
import {
  Portal,
  ClickOutside,
  ClickOutsideProvider,
  springConfigs,
  RawSpinner,
  KeyPressContextProvider,
  cx,
} from "@cdx/common";
import type {SpringValue} from "react-spring";
import {animated, useTransition} from "react-spring";
import FocusLock from "react-focus-lock";
import dsStyles from "@cdx/ds/css/index.css";
import type {BoxProps} from "@cdx/ds";
import {Box, DSIconButton, DSIcon, css, Col} from "@cdx/ds";
import {modalStyles as styles} from "./Modal.css";

let modalCount = 0;

// A modal without `onClose` cannot be dismissed. The Escape path in useClickOutside calls the
// handler unguarded, so it needs a function rather than undefined.
const noop = () => {};

export const focusLockKeyGroupContext = createContext<string | null>(null);
let nextIdx = 1;

const withModalClassNames = dsStyles.overflow.hidden.split(" ");

const stopPropagation = (e: SyntheticEvent) => {
  e.stopPropagation();
};

export const preventPropagationHandlers = {
  onClick: stopPropagation,
  onMouseDown: stopPropagation,
  onMouseMove: stopPropagation,
  onTouchStart: stopPropagation,
  onTouchMove: stopPropagation,
  onContextMenu: stopPropagation,
};

const ModalContainerContext = createContext<RefObject<HTMLDivElement>>({current: null});
export const useModalContainerRef = () => useContext(ModalContainerContext);

type ColorTheme = NonNullable<BoxProps["colorTheme"]>;

export type ModalOptions = {
  onClose?: () => void;
  hideClose?: boolean;
  width?: number | string;
  cdxContext?: string;
  purpleBackdrop?: boolean;
  blurBackdrop?: boolean;
  blueBackdrop?: boolean;
  colorTheme?: ColorTheme;
  allowEventPropagation?: boolean;
  adaptWidthToContent?: boolean;
};

type ModalProps = ModalOptions & {
  presenceProps: {value: SpringValue<number>};
  fixedHeight?: boolean;
  children: ReactNode;
};

const Modal = ({
  presenceProps,
  onClose,
  hideClose,
  children,
  width,
  cdxContext,
  purpleBackdrop,
  blurBackdrop,
  blueBackdrop,
  fixedHeight,
  colorTheme = "white",
  allowEventPropagation,
  adaptWidthToContent,
}: ModalProps) => {
  const focusGroupIdxRef = useRef<number | null>(null);
  // eslint-disable-next-line react-hooks/globals
  if (!focusGroupIdxRef.current) focusGroupIdxRef.current = nextIdx += 1;
  const focusGroupName = `modal_${focusGroupIdxRef.current}`;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (modalCount === 0) {
      if (document.body.classList) {
        document.body.classList.add(...withModalClassNames);
      }
    }
    modalCount += 1;
    return () => {
      modalCount -= 1;
      if (modalCount === 0) {
        if (document.body.classList) document.body.classList.remove(...withModalClassNames);
      }
    };
  }, []);

  const pointerEvents = presenceProps.value.to((val) => (val > 0.9 ? "initial" : "none"));

  return (
    <ModalContainerContext.Provider value={containerRef}>
      <ClickOutsideProvider>
        {(outsideClickHandlers) => (
          <>
            <animated.div
              style={{opacity: presenceProps.value, pointerEvents}}
              className={css(
                {position: "fixed", inset: "0"},
                styles.backdrop.base,
                purpleBackdrop && styles.backdrop.purple,
                blueBackdrop && styles.backdrop.blue,
                blurBackdrop && styles.backdrop.blurBackdrop
              )}
              {...(!allowEventPropagation && {...preventPropagationHandlers})}
              {...outsideClickHandlers}
            />
            <FocusLock group={focusGroupName} returnFocus>
              <focusLockKeyGroupContext.Provider value={focusGroupName}>
                <KeyPressContextProvider>
                  <animated.div
                    className={css({position: "fixed", inset: "0"}, styles.outer)}
                    {...(!allowEventPropagation && {...preventPropagationHandlers})}
                    {...outsideClickHandlers}
                    style={{pointerEvents}}
                    ref={containerRef}
                  >
                    <animated.div
                      style={{
                        opacity: presenceProps.value,
                        transform: presenceProps.value.to(
                          (val) => `translateY(${-50 * (1 - val)}px)`
                        ),
                        pointerEvents,
                      }}
                      data-cdx-context={cdxContext}
                      className={css(
                        {position: "absolute", inset: "0", display: "flex", flexDir: "column"},
                        styles.container.base,
                        fixedHeight && styles.container.fixedHeight
                      )}
                    >
                      <ClickOutside onClickOutside={onClose ?? noop}>
                        {(handlers) => (
                          <Col
                            className={cx(
                              styles.content.base,
                              !adaptWidthToContent && !width && styles.content.defaultWidth,
                              fixedHeight && styles.content.fixedHeight
                            )}
                            elevation={200}
                            relative
                            rounded="sm"
                            colorTheme={colorTheme}
                            bg
                            {...handlers}
                            style={width ? {width} : undefined}
                          >
                            <Suspense
                              fallback={
                                <Col flex="auto" align="center" justify="center" pa="24px">
                                  <RawSpinner />
                                </Col>
                              }
                            >
                              {!hideClose && (
                                <Box absolute style={{top: 18, right: 18}}>
                                  <DSIconButton
                                    icon={<DSIcon name="Close" label="Close" />}
                                    onClick={onClose}
                                    variant="tertiary"
                                    negatePadding
                                  />
                                </Box>
                              )}
                              {children}
                            </Suspense>
                          </Col>
                        )}
                      </ClickOutside>
                    </animated.div>
                  </animated.div>
                </KeyPressContextProvider>
              </focusLockKeyGroupContext.Provider>
            </FocusLock>
          </>
        )}
      </ClickOutsideProvider>
    </ModalContainerContext.Provider>
  );
};

/** Per-item overrides. Only ModalRegistry sets these, so any other item is treated as having none. */
type ModalItemHooks = {
  getWidth?: () => number | string | undefined;
  getColorTheme?: () => ColorTheme | undefined;
  getFixedHeight?: () => boolean | undefined;
};

const getItemHooks = (item: unknown): ModalItemHooks =>
  typeof item === "object" && item !== null ? (item as ModalItemHooks) : {};

type UseModalWithDataOptions<T> = ModalOptions & {
  extractKey?: (item: T) => unknown;
};

/**
 * Renders a modal while `data` is truthy. The returned function takes a render callback that
 * receives the (non-null) data; the modal animates out when `data` turns falsy.
 */
export const useModalWithData = <T,>(
  data: T,
  {extractKey = (i) => i, ...opts}: UseModalWithDataOptions<T>
) => {
  const transition = useTransition(data, {
    // react-spring's types insist on string | number keys, but it only ever uses them as Map keys
    key: extractKey as (item: T) => string | number,
    from: {value: 0},
    enter: {value: 1},
    leave: {value: 0.001},
    config: springConfigs.quickNoBounce,
  });
  return (renderFn: (item: NonNullable<T>) => ReactNode): ReactNode =>
    transition((props, item) => {
      if (!item) return null;
      const hooks = getItemHooks(item);
      return (
        <Portal>
          <Modal
            presenceProps={props}
            onClose={opts.onClose}
            hideClose={opts.hideClose}
            width={hooks.getWidth ? hooks.getWidth() : opts.width}
            cdxContext={opts.cdxContext}
            colorTheme={hooks.getColorTheme ? hooks.getColorTheme() : opts.colorTheme}
            fixedHeight={hooks.getFixedHeight?.()}
            blueBackdrop={opts.blueBackdrop}
            purpleBackdrop={opts.purpleBackdrop}
            blurBackdrop={opts.blurBackdrop}
            allowEventPropagation={opts.allowEventPropagation}
            adaptWidthToContent={opts.adaptWidthToContent}
          >
            {renderFn(item)}
          </Modal>
        </Portal>
      );
    });
};
